import { useState, useMemo, useRef, useEffect } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { Search, Heart, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { catalogApi, favoritesApi } from '../lib/api.js';
import { useTimezone } from '../hooks/useTimezone.js';
import { usePlayerStore, useAuthStore, useUiStore } from '../lib/stores.js';
import { formatTime } from '../lib/time.js';
import styles from './ChannelList.module.css';

// Strip trailing quality suffix to identify variants of the same channel.
// Mirrors the auto-link normalizer on the backend but is conservative —
// region tags (Hu/CE/Europe) are kept so different regional channels stay
// separate in the sidebar. Only HD/SD/UHD/FHD/4K/8K/HEVC suffixes collapse.
const QUALITY_SUFFIX_RE = /[\s\-_\.]+(uhd|fhd|hd|sd|4k|8k|hevc)$/i;
const QUALITY_LABEL_RE = /([\s\-_\.]+)(uhd|fhd|hd|sd|4k|8k|hevc)$/i;

function stripQuality(name) {
  if (!name) return '';
  let n = name;
  // Apply twice for stacked cases like "AMC Hu HD" -> "AMC Hu".
  for (let i = 0; i < 2; i += 1) {
    const next = n.replace(QUALITY_SUFFIX_RE, '');
    if (next === n) break;
    n = next;
  }
  return n.trim();
}

function qualityLabel(name) {
  const m = name?.match(QUALITY_LABEL_RE);
  return m ? m[2].toUpperCase() : 'SD';
}

function groupKey(name) {
  return stripQuality(name || '').toLowerCase().replace(/\s+/g, ' ').trim();
}

// Quality ranking decides which variant a fresh group defaults to. Highest
// available wins; if no variants have a quality tag at all, the first
// channel returned by the API takes the slot.
const QUALITY_RANK = { '4K': 5, UHD: 5, FHD: 4, HD: 3, HEVC: 2, SD: 1 };

function bestVariant(variants) {
  return variants.reduce((best, v) =>
    (QUALITY_RANK[v.quality] || 0) > (QUALITY_RANK[best.quality] || 0) ? v : best,
  variants[0]);
}


export default function ChannelList() {
  const [category, setCategory] = useState('');
  const [q, setQ] = useState('');
  const debouncedQ = useDebounce(q, 250);
  const token = useAuthStore((s) => s.token);

  const categoriesQ = useQuery({
    queryKey: ['categories', 'live'],
    queryFn: () => catalogApi.categories({ kind: 'live' }),
  });

  const channelsQ = useQuery({
    queryKey: ['channels', { category, q: debouncedQ }],
    queryFn: () => catalogApi.channels({ category, q: debouncedQ, page_size: 200 }),
    keepPreviousData: true,
  });

  const nowQ = useQuery({
    queryKey: ['channels-now'],
    queryFn: catalogApi.channelsNow,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });

  const favoritesQ = useQuery({
    queryKey: ['favorites', 'channel'],
    queryFn: () => favoritesApi.list('channel'),
    enabled: !!token,
  });

  const favoriteIdByChannel = useMemo(() => {
    const map = new Map();
    (favoritesQ.data?.results || []).forEach((f) => {
      if (f.item?.id) map.set(f.item.id, f.id);
    });
    return map;
  }, [favoritesQ.data]);

  // Group channels by quality-stripped key. Each group's variants are sorted
  // by quality rank desc so HD shows first in the pill row.
  const groups = useMemo(() => {
    const acc = new Map();
    (channelsQ.data?.results || []).forEach((channel) => {
      const key = groupKey(channel.name);
      if (!key) return;
      const variant = { channel, quality: qualityLabel(channel.name) };
      const group = acc.get(key);
      if (group) group.variants.push(variant);
      else acc.set(key, { key, name: stripQuality(channel.name) || channel.name, variants: [variant] });
    });
    return Array.from(acc.values()).map((g) => ({
      ...g,
      variants: g.variants.sort((a, b) => (QUALITY_RANK[b.quality] || 0) - (QUALITY_RANK[a.quality] || 0)),
    }));
  }, [channelsQ.data]);

  const nowMap = nowQ.data?.results || {};
  // Treat the API's server_time as authoritative for progress calculations,
  // so the bar doesn't drift if the client clock is off.
  const serverTime = nowQ.data?.server_time;

  return (
    <aside className={styles.aside} aria-label="Channels">
      <div className={styles.controls}>
        <CategoryDropdown
          categories={categoriesQ.data || []}
          value={category}
          onChange={setCategory}
        />
        <div className={styles.searchWrap}>
          <Search size={14} className={styles.searchIcon} />
          <input
            className={styles.searchInput}
            placeholder="Search channels"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </div>

      <div className={styles.list}>
        {channelsQ.isLoading && (
          <div className={styles.loading}><Loader2 className={styles.spin} size={18} /></div>
        )}
        {channelsQ.error && <div className={styles.empty}>Failed to load channels.</div>}
        {channelsQ.data?.results?.length === 0 && <div className={styles.empty}>No channels match.</div>}
        {groups.map((group) => (
          <ChannelGroupRow
            key={group.key}
            group={group}
            nowMap={nowMap}
            serverTime={serverTime}
            favoriteIdByChannel={favoriteIdByChannel}
          />
        ))}
      </div>
    </aside>
  );
}

function ChannelGroupRow({ group, nowMap, serverTime, favoriteIdByChannel }) {
  const selectChannel = usePlayerStore((s) => s.selectChannel);
  const playingChannel = usePlayerStore((s) => s.channel);
  const token = useAuthStore((s) => s.token);
  const openModal = useUiStore((s) => s.openModal);
  const tz = useTimezone();
  const qc = useQueryClient();

  // Active variant: whichever the user last picked, OR the best-quality one.
  // If the player is currently playing one of this group's variants, that
  // one stays sticky-active so the pill highlight tracks the player.
  const playingInGroup = group.variants.find((v) => v.channel.id === playingChannel?.id);
  const [activeId, setActiveId] = useState(() => (playingInGroup?.channel.id || bestVariant(group.variants).channel.id));
  const activeVariant =
    group.variants.find((v) => v.channel.id === activeId) ||
    playingInGroup ||
    bestVariant(group.variants);
  const channel = activeVariant.channel;

  const favoriteId = favoriteIdByChannel.get(channel.id);
  const isFavorite = !!favoriteId;
  const playing = playingChannel?.id === channel.id;

  const toggleFav = useMutation({
    mutationFn: async () => {
      if (!token) { openModal('login'); return; }
      if (isFavorite && favoriteId) await favoritesApi.remove(favoriteId);
      else await favoritesApi.add('channel', channel.id);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['favorites', 'channel'] }),
  });

  const now = nowMap[channel.id] || null;
  const progressPct = useMemo(() => progressForProgram(now, serverTime), [now, serverTime]);
  const timeRange = now
    ? `${formatTime(now.start_at, tz)} – ${formatTime(now.end_at, tz)}`
    : '';

  return (
    <div
      className={`${styles.row} ${playing ? styles.rowActive : ''}`}
      onClick={() => selectChannel(channel)}
    >
      <div className={styles.logo}>
        {channel.logo_url
          ? <img src={channel.logo_url} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          : <span className={styles.logoFallback}>{(group.name || '?').slice(0, 2).toUpperCase()}</span>}
      </div>

      <div className={styles.meta}>
        <div className={styles.nameRow}>
          <span className={styles.name}>{group.name}</span>
          {group.variants.length > 1 && (
            <div className={styles.variants}>
              {group.variants.map((v) => (
                <button
                  key={v.channel.id}
                  type="button"
                  className={`${styles.variantPill} ${v.channel.id === activeVariant.channel.id ? styles.variantPillOn : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveId(v.channel.id);
                    if (playing) selectChannel(v.channel); // hot-swap if it's the one playing
                  }}
                >
                  {v.quality}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className={styles.sub}>
          {now ? (
            <>
              <span className={styles.subTime}>{timeRange}</span>
              <span className={styles.subTitle}> · {now.title}</span>
            </>
          ) : (
            <span className={styles.subMuted}>{channel.category?.name || 'No EPG'}</span>
          )}
        </div>
      </div>

      <button
        className={`${styles.favBtn} ${isFavorite ? styles.favOn : ''}`}
        onClick={(e) => { e.stopPropagation(); toggleFav.mutate(); }}
        aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
        disabled={toggleFav.isPending}
      >
        <Heart size={15} fill={isFavorite ? 'currentColor' : 'none'} />
      </button>

      {progressPct !== null && (
        <div className={styles.progress}>
          <div className={styles.progressFill} style={{ width: `${progressPct}%` }} />
        </div>
      )}
    </div>
  );
}

// Percent (0..100) of the way through the current program. Returns null
// when there's no EPG so the row can hide the bar.
function progressForProgram(now, serverTime) {
  if (!now) return null;
  const start = new Date(now.start_at).getTime();
  const end = new Date(now.end_at).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
  const ref = serverTime ? new Date(serverTime).getTime() : Date.now();
  const pct = ((ref - start) / (end - start)) * 100;
  if (!Number.isFinite(pct)) return null;
  return Math.max(0, Math.min(100, pct));
}

function CategoryDropdown({ categories, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  const current = categories.find((c) => c.slug === value);
  return (
    <div className={styles.dropdown} ref={ref}>
      <button className={styles.dropdownBtn} onClick={() => setOpen((o) => !o)}>
        <span>{current ? current.name : 'All categories'}</span>
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className={styles.dropdownMenu}>
          <button className={styles.dropdownItem} onClick={() => { onChange(''); setOpen(false); }}>
            <ChevronRight size={14} /> All categories
          </button>
          {categories.map((c) => (
            <button
              key={c.id}
              className={`${styles.dropdownItem} ${c.slug === value ? styles.dropdownItemActive : ''}`}
              onClick={() => { onChange(c.slug); setOpen(false); }}
            >
              <ChevronRight size={14} /> {c.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}
